import { FieldDescription } from "./FieldDescription";
import { ReadOnlyValue } from "./ReadOnlyValue";
import type { CustomFieldValues } from "@forgepoint/shared";
import { cn } from "@/lib/utils";

/** Props for the ReadOnlyField component. */
interface ReadOnlyFieldProps {
  fieldName: string;
  value: CustomFieldValues[string];
  format?: string;
  type: "string" | "number" | "array";
  inline: boolean;
  description?: string;
  compact?: boolean;
  required?: boolean;
}

/**
 * Displays a single custom field as a label/value pair in read-only mode.
 * Resolves user IDs to display names and formats dates.
 * @param fieldName - The label to display
 * @param value - The raw field value
 * @param format - Optional format hint (e.g. "fpUser", "date")
 * @param type - The primitive type ("string" or "number")
 * @param inline - Whether to render in the inline (large) style or standalone (small) style
 * @param description - Optional helper text displayed below the value
 * @param required - Whether the field is marked required in the schema
 */
export function ReadOnlyField({
  fieldName,
  value,
  format: fieldFormat,
  type,
  inline,
  description,
  compact = false,
  required = false,
}: ReadOnlyFieldProps) {
  const requiredMark = required ? (
    <span className="ml-0.5 text-destructive">*</span>
  ) : null;

  if (inline) {
    return (
      <div>
        <p className="text-sm text-muted-foreground mb-2">
          {fieldName}
          {requiredMark}
        </p>
        <ReadOnlyValue value={value} format={fieldFormat} type={type} inline />
        <FieldDescription description={description} />
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <p
        className={cn(
          "text-xs font-medium",
          compact ? "text-foreground" : "text-muted-foreground",
        )}
      >
        {fieldName}
        {requiredMark}
      </p>
      <ReadOnlyValue
        value={value}
        format={fieldFormat}
        type={type}
        inline={false}
      />
      <FieldDescription description={description} />
    </div>
  );
}
